import { useSelector } from 'app-redux'
import { RootState } from 'common/redux'
import { redux as scriptRedux } from 'features/script'
import { useEffect, useState } from 'react'
import { LobbyMoment, stillAhead } from './lobby-moment'
import { useLobbyMoment } from './use-lobby-moment'

export type InPersonLobbyMoment = {
	/** `undefined` while the phase is being read. */
	moment?: LobbyMoment
	/**
	 * When the room is expected to start or come back, if the script says and
	 * that time is still ahead.
	 */
	at?: Date
}

/** `setTimeout` takes a signed 32-bit delay; anything longer fires at once. */
const MAX_TIMEOUT_MS = 2 ** 31 - 1

/**
 * Where the agenda is: the item being run or, before the script has started
 * (the server reports no current item then), the first one.
 */
const selectPlannedStart = (state: RootState): Date | undefined =>
	scriptRedux.selectCurrentScriptStep(state)?.item.startTime ??
	state.script.chunks[state.script.currentSessionChunkIndex]?.items[0]
		?.startTime

/**
 * Which lobby this is (`useLobbyMoment`), and the time that goes with it: the
 * first agenda item before the start, the next one during a break.
 */
export const useInPersonLobbyMoment = (): InPersonLobbyMoment => {
	const moment = useLobbyMoment()

	const current = useSelector(selectPlannedStart)
	const next = useSelector(scriptRedux.selectNextScriptStep)?.item.startTime

	const planned =
		moment === 'not_started' ? current : moment === 'break' ? next : undefined

	// Re-render once the planned time passes, so it disappears on its own.
	const [, setPassed] = useState(0)
	useEffect(() => {
		const wait = planned ? planned.getTime() - Date.now() : 0
		if (wait <= 0) {
			return
		}
		const timer = setTimeout(
			() => setPassed(count => count + 1),
			Math.min(wait + 1, MAX_TIMEOUT_MS)
		)
		return () => clearTimeout(timer)
	}, [planned])

	return { moment, at: stillAhead(planned, Date.now()) }
}
